import {
  DEFAULT_TASK_LIST_LAYOUT,
  TASK_LIST_BUILTIN_IDS,
  type TaskListBuiltinId,
  type TaskListSectionEntry,
} from '../types/taskListLayout.js';
import type { Task } from '../types/task.js';

const MAX_SECTIONS = 24;

function isBuiltinId(v: unknown): v is TaskListBuiltinId {
  return typeof v === 'string' && (TASK_LIST_BUILTIN_IDS as readonly string[]).includes(v);
}

/**
 * Parse and validate layout from DB JSON. Returns null to mean "use DEFAULT_TASK_LIST_LAYOUT at render time".
 */
export function normalizeTaskListLayout(raw: unknown): TaskListSectionEntry[] | null {
  if (raw == null) return null;
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_SECTIONS) return null;

  const out: TaskListSectionEntry[] = [];
  const seenBuiltin = new Set<TaskListBuiltinId>();
  const seenCustom = new Set<string>();

  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const kind = (row as { kind?: unknown }).kind;
    if (kind === 'builtin') {
      const builtinId = (row as { builtinId?: unknown }).builtinId;
      if (!isBuiltinId(builtinId) || seenBuiltin.has(builtinId)) continue;
      seenBuiltin.add(builtinId);
      const icon = (row as { icon?: unknown }).icon;
      const title = (row as { title?: unknown }).title;
      out.push({
        kind: 'builtin',
        builtinId,
        icon: typeof icon === 'string' && icon.length <= 16 ? icon : undefined,
        title: typeof title === 'string' && title.trim().length > 0 && title.length <= 120 ? title.trim() : undefined,
      });
    } else if (kind === 'custom') {
      const id = (row as { id?: unknown }).id;
      const title = (row as { title?: unknown }).title;
      const tagIds = (row as { tagIds?: unknown }).tagIds;
      if (typeof id !== 'string' || id.length < 8 || seenCustom.has(id)) continue;
      if (typeof title !== 'string' || title.trim().length === 0 || title.length > 120) continue;
      if (!Array.isArray(tagIds) || tagIds.length === 0) continue;
      const cleanTags = tagIds.filter((t): t is string => typeof t === 'string' && t.length > 0);
      if (cleanTags.length === 0) continue;
      seenCustom.add(id);
      const icon = (row as { icon?: unknown }).icon;
      out.push({
        kind: 'custom',
        id,
        title: title.trim(),
        icon: typeof icon === 'string' && icon.length <= 16 ? icon : undefined,
        tagIds: cleanTags,
      });
    }
  }

  return out.length > 0 ? out : null;
}

export function getEffectiveTaskListLayout(stored: TaskListSectionEntry[] | null | undefined): TaskListSectionEntry[] {
  if (stored && stored.length > 0) return stored;
  return DEFAULT_TASK_LIST_LAYOUT;
}

/** Team has the Party (temporary event) section enabled in task list layout. */
export function layoutIncludesPartyEvent(layout: TaskListSectionEntry[] | null | undefined): boolean {
  const list = getEffectiveTaskListLayout(layout ?? null);
  return list.some((e) => e.kind === 'builtin' && e.builtinId === 'party_event');
}

/** True if the task carries any of the given tag ids (union of legacy `tagId` and `tagIds`). */
function taskHasAnyTag(task: Task, tagIds: string[]): boolean {
  const onTask = new Set<string>();
  if (task.tagId) onTask.add(task.tagId);
  for (const id of task.tagIds ?? []) {
    if (id) onTask.add(id);
  }
  return tagIds.some((id) => onTask.has(id));
}

/**
 * Tasks pulled into custom sections (first matching section wins). Excludes pinned tasks.
 */
export function collectCustomSectionClaims(
  layout: TaskListSectionEntry[],
  poolTasks: Task[]
): Map<string, Task[]> {
  const bySection = new Map<string, Task[]>();
  const claimed = new Set<string>();

  for (const entry of layout) {
    if (entry.kind !== 'custom') continue;
    const matched: Task[] = [];
    for (const task of poolTasks) {
      if (claimed.has(task.id)) continue;
      if (taskHasAnyTag(task, entry.tagIds)) {
        matched.push(task);
        claimed.add(task.id);
      }
    }
    bySection.set(entry.id, matched);
  }
  return bySection;
}

export function filterOutClaimed(tasks: Task[], claimed: Set<string>): Task[] {
  return tasks.filter((t) => !claimed.has(t.id));
}

export function splitTasksByType(tasks: Task[]): { daily: Task[]; special: Task[] } {
  const daily: Task[] = [];
  const special: Task[] = [];
  for (const t of tasks) {
    if (t.type === 'daily') daily.push(t);
    else if (t.type === 'special') special.push(t);
  }
  return { daily, special };
}

export function builtinDefaultIcon(id: TaskListBuiltinId): string {
  switch (id) {
    case 'pinned':
      return '📌';
    case 'task_review':
      return '✅';
    case 'personal_day':
      return '⭐';
    case 'team_day':
      return '👥';
    case 'today_daily':
      return '☀️';
    case 'upcoming':
      return '🗓️';
    case 'today_special':
      return '⭐';
    case 'party_event':
      return '🎉';
    default:
      return '📋';
  }
}
